import { ZodTypeAny, ZodBoolean, ZodEnum, ZodType, ZodOptional } from 'zod'
import { Expression, ShapeBase } from '~/prix/types/expression'
import { getMeta, ZodTypeWithMeta } from '~/prix/types/zodType'

export function prepareAssertions(expression: ZodType & ZodTypeWithMeta): Record<
  string,
  {
    expression: Expression
    label: string
  }
> {
  const shape = expression?.getMeta()?.shape as ShapeBase
  if (!shape) {
    return {}
  }

  return Object.entries(shape).reduce(
    (acc, [key, type]) => {
      let schema = type as ZodTypeAny

      if (schema instanceof ZodOptional) schema = schema.unwrap()
      if (!schema.description) return acc

      // Boolean
      if (schema instanceof ZodBoolean) {
        const expression: Expression = {
          type: 'expression',
          kind: 'binary',
          left: {
            type: 'expression',
            kind: 'property',
            property: key,
          },
          operator: '=',
          right: {
            type: 'expression',
            kind: 'literal',
            literalType: 'boolean',
            value: true,
          },
        }
        acc[key] = {
          expression,
          label: schema.description,
        }
      }

      // Enumerated
      if (schema instanceof ZodEnum) {
        const options = schema._def.values as string[]
        const keysAndLabels = getMeta(schema)?.keysAndLabels as Record<string, string> | undefined

        for (const enumValue of options) {
          const fullKey = `${key}_${enumValue}`
          const expression: Expression = {
            type: 'expression',
            kind: 'binary',
            left: {
              type: 'expression',
              kind: 'property',
              property: key,
            },
            operator: '=',
            right: {
              type: 'expression',
              kind: 'literal',
              literalType: 'string',
              value: enumValue,
            },
          }
          acc[fullKey] = {
            expression,
            label: `${keysAndLabels?.[enumValue] ?? enumValue} (${schema.description})`,
          }
        }
      }

      return acc
    },
    {} as Record<
      string,
      {
        expression: Expression
        label: string
      }
    >,
  )
}
